/**
 * AI CONTEXT
 * Purpose: live verification of `portfolios/{studentId}` + related student/chat data.
 * Key deps: portfolioAtoms, studentAtoms, portfolioCommentAtoms, useOneDoc, useFilteredDocs.
 * Referenced by: App route /portfolio-debug (debug-gated).
 * Importance: manual TDD while exercising Portfolio Management UI.
 */
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExperimentOutlined,
} from "@ant-design/icons"
import { App, Button, Card, Col, Input, Row, Typography } from "antd"
import { useCallback, useMemo, useState } from "react"

import { portfolioAtoms, portfolioCommentAtoms, studentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { NULL_ID } from "#classes/Firebase"
import PortfolioManagementModal from "#components/PortfolioManagement/PortfolioManagementModal"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import {
  PortfolioCommentConstructor,
  StudentPortfolioConstructor,
} from "#customTypes/composedTypes/portfolioTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useUpdateDoc } from "#hooks/jotai/useSaveDoc"
import { useFilteredDocs, useOneDoc } from "#hooks/jotai/useSubscription"
import DataDebugger from "#mocks/DataDebugger"
import { clearPortfolioTestDataForStudent } from "#utils/clearPortfolioTestDataForStudent"

const STORAGE_KEY = "jarvis-portfolio-debug-student-id"
/** Align with EvalDebugDashboard default student for cross-checking. */
const DEFAULT_STUDENT_ID = "sT1mXjJEOgBYgrcnWYmh"

const { Title, Text } = Typography

const readStoredStudentId = (): string => {
  try {
    const v = localStorage.getItem(STORAGE_KEY)?.trim()
    return v && v.length > 0 ? v : DEFAULT_STUDENT_ID
  } catch {
    return DEFAULT_STUDENT_ID
  }
}

const SaveStatus = ({ label, ok }: { label: string; ok: boolean }) => {
  const theme = useThemeContext()
  return (
    <Flex gap={8} style={{ alignItems: "center" }}>
      {ok ? (
        <CheckCircleFilled style={{ color: theme.eric.green, fontSize: 18 }} />
      ) : (
        <CloseCircleFilled style={{ color: theme.eric.red, fontSize: 18 }} />
      )}
      <Text strong>{label}</Text>
    </Flex>
  )
}

const PortfolioDebugDashboard = () => {
  const { message, modal } = App.useApp()
  const { updateDoc: updateStudent, isLoading: isClearingPortfolio } =
    useUpdateDoc(DOC_TYPES.STUDENTS)

  const [inputId, setInputId] = useState(readStoredStudentId)
  const [activeStudentId, setActiveStudentId] = useState<Uuid>(() =>
    readStoredStudentId()
  )
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false)

  const watchId = activeStudentId?.trim() ? activeStudentId : NULL_ID

  const student = useOneDoc<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS,
    watchId
  )

  const portfolio = useOneDoc<StudentPortfolioConstructor>(
    portfolioAtoms,
    DOC_TYPES.PORTFOLIOS,
    watchId
  )

  const commentQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        studentId: {
          value: watchId,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [watchId]
  )

  const portfolioComments = useFilteredDocs<PortfolioCommentConstructor>(
    portfolioCommentAtoms,
    DOC_TYPES.PORTFOLIO_COMMENTS,
    commentQuery
  )

  const applyStudentId = useCallback(() => {
    const next = inputId.trim()
    if (!next) {
      void message.warning("Enter a student id")
      return
    }
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      void message.error("Could not save id to localStorage")
      return
    }
    setActiveStudentId(next)
    void message.success(`Watching student ${next}`)
  }, [inputId, message])

  const handleClearPortfolioTestData = useCallback(() => {
    const sid = activeStudentId.trim()
    if (!sid || !student || student.id !== sid) {
      void message.warning("Load a valid student before clearing.")
      return
    }
    modal.confirm({
      title: "Clear portfolio test data for this student?",
      content: (
        <Flex col gap={8}>
          <span>Only portfolio-related data is removed:</span>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              Deletes <Text code>portfolios/{sid}</Text>
            </li>
            <li>Deletes all portfolioComments with this studentId</li>
            <li>
              Resets each artwork entry: inPortfolio=false, removes projectId /
              sortIndex (keeps URLs and files)
            </li>
          </ul>
          <Text type="secondary">
            Does not change skill history, attendance, classes, evaluations, or
            Storage objects.
          </Text>
        </Flex>
      ),
      okText: "Clear portfolio data",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const artworkPatch = await clearPortfolioTestDataForStudent(student)
          if (artworkPatch) {
            const id = await updateStudent(student.id, artworkPatch)
            if (id == null) return
          }
          void message.success("Portfolio test data cleared.")
        } catch (e) {
          void message.error(
            e instanceof Error ? e.message : "Clear failed unexpectedly."
          )
        }
      },
    })
  }, [activeStudentId, message, modal, updateStudent, student])

  const checks = useMemo(() => {
    const sid = activeStudentId?.trim()
    const hasSid = Boolean(sid)
    const studentOk = Boolean(student && sid && student.id === sid)
    const portfolioExists = Boolean(portfolio && sid)

    const idsAligned =
      portfolioExists && portfolio!.id === sid && portfolio!.studentId === sid

    const profile = portfolio?.profile
    const hasProfile = Boolean(profile)
    const hasInquiry = Boolean(profile?.sustainedInquiryQuestion)
    const hasPractice = Boolean(profile?.sustainedPracticeEvidence)

    const projectKeys = portfolio ? Object.keys(portfolio.projects ?? {}) : []
    const projectCount = projectKeys.length
    const order = portfolio?.projectOrder ?? []
    const orderSet = new Set(order)
    const keySet = new Set(projectKeys)
    const orderMatchesKeys =
      portfolioExists &&
      order.length === keySet.size &&
      order.every((id) => keySet.has(id)) &&
      projectKeys.every((k) => orderSet.has(k))

    let stageCount = 0
    if (portfolio?.projects) {
      for (const p of Object.values(portfolio.projects)) {
        stageCount += Object.keys(p.stages ?? {}).length
      }
    }

    const timeline = portfolio?.timelineEntries ?? []
    const timelineIsArray = Array.isArray(timeline)
    const timelineHasAuthorKind =
      !timeline.length ||
      timeline.every(
        (e) =>
          e.authorKind === "STUDENT" ||
          e.authorKind === "TEACHER" ||
          e.authorKind === undefined
      )
    const timelineExplicitAuthorKind =
      timeline.length === 0 || timeline.every((e) => e.authorKind != null)

    const lane = portfolio?.priorityLaneProjectIds ?? []
    const laneValid = lane.every((pid) => keySet.has(pid))

    const hasDbMeta =
      portfolioExists &&
      Boolean(portfolio!.createdAt && portfolio!.lastUpdatedAt)

    const artworkKeys = student?.artwork ? Object.keys(student.artwork) : []
    const artworkCount = artworkKeys.length

    return {
      hasSid,
      studentOk,
      portfolioExists,
      idsAligned,
      hasProfile,
      hasInquiry,
      hasPractice,
      projectCount,
      orderMatchesKeys,
      stageCount,
      timelineIsArray,
      timelineLength: timeline.length,
      timelineHasAuthorKind,
      timelineExplicitAuthorKind,
      laneValid,
      hasDbMeta,
      artworkCount,
      portfolioCommentsCount: portfolioComments.length,
    }
  }, [activeStudentId, portfolio, portfolioComments.length, student])

  return (
    <Flex
      col
      gap={16}
      style={{ padding: 40, maxWidth: 1200, margin: "0 auto" }}
    >
      <Title level={3}>
        <ExperimentOutlined /> Portfolio Debug Dashboard
      </Title>

      <Text type="secondary">
        Subscribes to <Text code>students/{`{id}`}</Text>,{" "}
        <Text code>portfolios/{`{id}`}</Text>, and{" "}
        <Text code>portfolioComments</Text> where <Text code>studentId</Text>{" "}
        matches. Open the portfolio UI below or use <Text code>/test</Text> and
        watch this page update live.
      </Text>

      {portfolioModalOpen && activeStudentId.trim() ? (
        <PortfolioManagementModal
          key={activeStudentId.trim()}
          isOpen
          handleCloseAttempt={() => setPortfolioModalOpen(false)}
          title={
            student?.name
              ? `Portfolio — ${student.name}`
              : "Portfolio management"
          }
          initialValues={{ id: activeStudentId.trim() }}
        />
      ) : null}

      <Card size="small" title="Student id (localStorage)">
        <Flex gap={8} style={{ flexWrap: "wrap", alignItems: "center" }}>
          <Input
            style={{ maxWidth: 420 }}
            value={inputId}
            onChange={(e) => setInputId(e.target.value)}
            placeholder="Student document id"
            onPressEnter={() => applyStudentId()}
          />
          <Button type="primary" onClick={applyStudentId}>
            Apply & save
          </Button>
          <Button
            type="default"
            disabled={!activeStudentId.trim()}
            onClick={() => setPortfolioModalOpen(true)}
          >
            Open portfolio
          </Button>
          <Button
            danger
            disabled={!checks.studentOk || isClearingPortfolio}
            loading={isClearingPortfolio}
            onClick={handleClearPortfolioTestData}
          >
            Clear portfolio test data
          </Button>
          <Text type="secondary">
            Key: <Text code>{STORAGE_KEY}</Text> — default{" "}
            <Text code>{DEFAULT_STUDENT_ID}</Text>
          </Text>
        </Flex>
        <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
          Active: <Text code>{activeStudentId}</Text>
        </Text>
      </Card>

      <Card size="small" title="Live checks">
        <Row gutter={[16, 12]}>
          <Col span={12}>
            <SaveStatus label="Non-empty student id" ok={checks.hasSid} />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="Student doc exists in Firestore"
              ok={checks.studentOk}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="Portfolio doc exists (portfolios/{id})"
              ok={checks.portfolioExists}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="Portfolio id & studentId match active student"
              ok={checks.idsAligned}
            />
          </Col>
          <Col span={12}>
            <SaveStatus label="profile object present" ok={checks.hasProfile} />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="profile.sustainedInquiryQuestion present"
              ok={checks.hasInquiry}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="profile.sustainedPracticeEvidence present"
              ok={checks.hasPractice}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="createdAt / lastUpdatedAt on portfolio"
              ok={checks.hasDbMeta}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label={`Projects loaded (count: ${checks.projectCount})`}
              ok={checks.projectCount > 0}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="projectOrder matches project keys"
              ok={checks.orderMatchesKeys}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label={`Stages total (count: ${checks.stageCount})`}
              ok={checks.stageCount > 0}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="timelineEntries is array"
              ok={checks.timelineIsArray}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label={`Timeline rows (count: ${checks.timelineLength})`}
              ok={checks.timelineLength > 0}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="Timeline authorKind valid (STUDENT | TEACHER | unset)"
              ok={checks.timelineHasAuthorKind}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="All timeline rows have explicit authorKind (or empty)"
              ok={checks.timelineExplicitAuthorKind}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label="priorityLaneProjectIds ⊆ project ids"
              ok={checks.laneValid}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label={`Student artwork entries (count: ${checks.artworkCount})`}
              ok={checks.artworkCount > 0}
            />
          </Col>
          <Col span={12}>
            <SaveStatus
              label={`portfolioComments for student (count: ${checks.portfolioCommentsCount})`}
              ok={checks.portfolioCommentsCount > 0}
            />
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Raw portfolio doc">
        <DataDebugger data={portfolio ?? null} logString="portfolio" />
      </Card>

      <Card size="small" title="Raw student doc (artwork / context)">
        <DataDebugger data={student ?? null} logString="student" />
      </Card>

      <Card size="small" title="portfolioComments (studentId query)">
        <DataDebugger data={portfolioComments} logString="portfolioComments" />
      </Card>
    </Flex>
  )
}

export default PortfolioDebugDashboard
